import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactReviewStatus } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactReviewStatus;
  isBoolean?: boolean;
  isDate?: boolean;
}

const REVIEW_STATUS_FIELDS: FieldMapping[] = [
  {
    apiField: 'contactRequiresReview',
    formField: 'contactRequiresReview',
    isBoolean: true
  },
  { apiField: 'identifiedOn', formField: 'identifiedOn', isDate: true },
  { apiField: 'statusDate', formField: 'statusDate', isDate: true },
  { apiField: 'lastCompletedStep', formField: 'lastCompletedStep' },
  { apiField: 'nextSteps', formField: 'nextSteps' },
  {
    apiField: 'requestedDoNotSendGifts',
    formField: 'requestedDoNotSendGifts',
    isBoolean: true
  },
  { apiField: 'rmOutlookSync', formField: 'rmOutlookSync', isBoolean: true }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactReviewStatus,
  initial: ContactReviewStatus
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isBoolean, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    let hasChanged = false;
    if (isDate) {
      hasChanged =
        toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string);
    } else {
      hasChanged = currentVal !== initialVal;
    }

    if (hasChanged) {
      if (isBoolean) {
        changes[apiField] = currentVal as boolean;
      } else if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        contactRequiresReview
        identifiedOn
        statusDate
        lastCompletedStep
        nextSteps
        requestedDoNotSendGifts
        rmOutlookSync
      }
    }
  }
`;

export interface EditReviewStatusDialogProps {
  contactId: string;
  initialValue: ContactReviewStatus;
  isOpen: boolean;
  onChange: (data: ContactReviewStatus) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactReviewStatus;
}

export function EditReviewStatusDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditReviewStatusDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(REVIEW_STATUS_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save review status');
      } else {
        toast.success('Review status saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving review status:', error);
      toast.error('Failed to save review status');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Review & Status"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.contactRequiresReview}
          label="Contact Requires Review"
          onCheckedChange={contactRequiresReview => {
            onChange({ ...value, contactRequiresReview });
          }}
        />
        <EditFormField
          label="Identified On"
          onChange={identifiedOn => {
            onChange({ ...value, identifiedOn });
          }}
          type="date"
          value={toDateInputValue(value.identifiedOn)}
        />
        <EditFormField
          label="Status Date"
          onChange={statusDate => {
            onChange({ ...value, statusDate });
          }}
          type="date"
          value={toDateInputValue(value.statusDate)}
        />
        <EditCheckboxField
          checked={value.requestedDoNotSendGifts}
          label="Requested do not send gifts"
          onCheckedChange={requestedDoNotSendGifts => {
            onChange({ ...value, requestedDoNotSendGifts });
          }}
        />
        <EditCheckboxField
          checked={value.rmOutlookSync}
          label="RM Outlook Sync"
          onCheckedChange={rmOutlookSync => {
            onChange({ ...value, rmOutlookSync });
          }}
        />
      </div>
      <div className="mt-4 space-y-4">
        <EditTextareaField
          label="Last Completed Step"
          onChange={lastCompletedStep => {
            onChange({ ...value, lastCompletedStep });
          }}
          rows={4}
          value={value.lastCompletedStep}
        />
        <EditTextareaField
          label="Next Steps"
          onChange={nextSteps => {
            onChange({ ...value, nextSteps });
          }}
          rows={4}
          value={value.nextSteps}
        />
      </div>
    </EditDialog>
  );
}
